import { useEffect } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent, Track } from "livekit-client";
import { nextLevelerGain, rmsDbfs } from "@/lib/micLeveler";

// Per remote voice: 150 ms playout cushion (jitter absorption) + Guest Mic
// Leveler — compressor + slow auto-gain so quiet and loud boxes land at the
// same loudness for the listener. Runs inside LiveKit's webAudioMix graph.
const AUDIO_PLAYOUT_DELAY_S = 0.15;
const LEVEL_TICK_MS = 300;

function attachLeveler(track, room) {
  const ctx = track.audioContext || room.audioContext;
  if (!ctx || typeof track.setWebAudioPlugins !== "function") return null;
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -28; comp.knee.value = 18; comp.ratio.value = 6;
  comp.attack.value = 0.005; comp.release.value = 0.25;
  const gain = ctx.createGain();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  track.setWebAudioPlugins([comp, gain, analyser]);
  const buf = new Float32Array(analyser.fftSize);
  const timer = setInterval(() => {
    analyser.getFloatTimeDomainData(buf);
    const next = nextLevelerGain(gain.gain.value, rmsDbfs(buf));
    gain.gain.setTargetAtTime(next, ctx.currentTime, 0.4);
  }, LEVEL_TICK_MS);
  return () => { clearInterval(timer); try { track.setWebAudioPlugins([]); } catch { /* gone */ } };
}

export default function RemoteAudioTuner({ levelerOff = true }) {
  const room = useRoomContext();

  // Guaranteed playback unlock: if the browser blocks remote audio (iOS
  // autoplay policy), the first tap anywhere in the room resumes it.
  useEffect(() => {
    if (!room) return undefined;
    let armed = false;
    const unlock = () => {
      if (room.canPlaybackAudio) return;
      room.startAudio().catch(() => {});
    };
    const arm = () => {
      if (armed || room.canPlaybackAudio) return;
      armed = true;
      document.addEventListener("pointerdown", unlock, { passive: true });
      document.addEventListener("touchend", unlock, { passive: true });
    };
    arm();
    room.on(RoomEvent.AudioPlaybackStatusChanged, arm);
    return () => {
      room.off(RoomEvent.AudioPlaybackStatusChanged, arm);
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("touchend", unlock);
    };
  }, [room]);

  useEffect(() => {
    if (!room) return undefined;
    const cleanups = new Map();
    const tune = (track) => {
      if (track?.kind !== Track.Kind.Audio || cleanups.has(track.sid)) return;
      try { track.setPlayoutDelay?.(AUDIO_PLAYOUT_DELAY_S); } catch { /* unsupported */ }
      // F2: with webAudioMix off the SDK never connects these nodes — the leveler is
      // inert, so skip allocating an AudioContext graph + timer per guest (flag-gated).
      if (levelerOff) return;
      try { const c = attachLeveler(track, room); if (c) cleanups.set(track.sid, c); } catch { /* no webaudio */ }
    };
    const untune = (track) => { cleanups.get(track?.sid)?.(); cleanups.delete(track?.sid); };
    room.on(RoomEvent.TrackSubscribed, tune);
    room.on(RoomEvent.TrackUnsubscribed, untune);
    room.remoteParticipants.forEach((p) => p.audioTrackPublications.forEach((pub) => tune(pub.track)));
    return () => {
      room.off(RoomEvent.TrackSubscribed, tune);
      room.off(RoomEvent.TrackUnsubscribed, untune);
      cleanups.forEach((c) => c());
    };
  }, [room, levelerOff]);

  return null;
}
